import axios from "axios";
import { useMediaQuery } from "@mui/material";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  const formattedToken = `Bearer ${token.replace(/"/g, "")}`;
  return {
    Authorization: formattedToken,
    "Content-Type": "application/json",
  };
};

export const fetchJsonDynamic = async (
  objectName = null,
  inputObjectName = null,
  params = null,
  baseUrl
) => {
  const headers = getAuthHeaders();

  // Construct the query dynamically based on the presence of params
  const query = {
    query: `
      {
        ${objectName}${
      params
        ? `(${inputObjectName}: {${Object.entries(params)
            .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
            .join(", ")}})`
        : ""
    } {
            results
            error
        }
      }
    `,
  };

  try {
    const response = await axios.post(baseUrl, query, { headers });
    return response?.data?.data?.[objectName];
  } catch (error) {
    return { error: "Failed to fetch data" };
  }
};

export const fetchCustomerStatementJsonDynamic = async (
  objectName = null,
  inputObjectName = null,
  params = null,
  baseUrl
) => {
  const headers = getAuthHeaders();

  // Convert params into a string of key-value pairs
  const formattedParams = Object.entries(params)
    .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
    .join(", ");

  // Construct the GraphQL query string properly
  const query = {
    query: `
      {
        ${objectName}(${formattedParams}) {
          results
          error
        }
      }
    `,
  };

  try {
    const response = await axios.post(baseUrl, query, { headers });
    return response?.data?.data?.[objectName];
  } catch (error) {
    return { error: "Failed to fetch data" };
  }
};

export const fetchJson = async ({
  objectName = null,
  inputObjectName = null,
  params = null,
  baseUrl,
}) => {
  const headers = getAuthHeaders();

  // Construct the query dynamically based on the presence of params
  const query = {
    query: `
      {
        ${objectName}${
      params
        ? `(${inputObjectName}: {${Object.entries(params)
            .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
            .join(", ")}})`
        : ""
    } {
          results
          error
        }
      }
    `,
  };

  try {
    const response = await axios.post(baseUrl, query, { headers });
    return response?.data?.data?.[objectName];
  } catch (error) {
    return { error: "Failed to fetch data" };
  }
};
const getRandomColumns = (columns, num) => {
  const shuffled = [...columns].sort(() => 0.5 - Math.random()); // Shuffle columns randomly
  return shuffled.slice(0, num); // Pick the first 'num' columns
};

export const generateColumnsFromResults = (
  resultsArray,
  MoreignoreFields,
  isMobile
) => {
  // Ensure there's data to process
  if (!resultsArray || resultsArray.length === 0) return [];

  // Get all keys (fields) from the first object in the results array
  const result = resultsArray[0];
  const allowedIsFields = [
    "IsGroup",
    "IsDepositRequired",
    "IsDepositPercentage",
    "IsPercentage",
    "IsApproved",
  ]; // Add any "is" fields you want to allow
  const allowedIdFields = ["TransId", "LoanId"];

  const ignoreNullCheck = MoreignoreFields ? MoreignoreFields : [];
  // Fields to ignore (including those passed in the "ignore" parameter)
  const ignoreFields = [
    "CreatedDate",
    "CreatedBy",
    "UpdatedDate",
    "UpdatedBy",
    //"isActive",
    //"CompanyName",
    ...(MoreignoreFields || []),
  ];

  // Get all keys (fields) from the result object
  const fields = Object.keys(result);

  // Map the extracted field names to the desired column format, ignoring specific fields
  let columns = fields
    .filter(
      (field) =>
        !ignoreFields.includes(field) && // Exclude predefined ignored fields
        (field.toLowerCase() === "id" ||
          !field.toLowerCase().endsWith("id") ||
          allowedIdFields.includes(field)) && // ✅ explicitly allow LoanId and TransId
        (!field.toLowerCase().startsWith("is") ||
          allowedIsFields.includes(field))
    )

    .map((field) => ({
      field: field === "Id" ? "id" : field, // Convert 'Id' to 'id'
      headerName: formatHeaderName(field === "Id" ? "id" : field), // Format header to be human-readable, 'Id' to 'id'
      flex: 1, // Adjust the column width
      minWidth: 100, // Ensures small content is not too squeezed
      maxWidth: 600, // Ensures content does not stretch too much
    }));
  if (isMobile) {
    // If mobile, pick only 3 random columns from the generated columns
    columns = getRandomColumns(columns, 2);
  }

  return columns;
};

// Helper function to format the header name (camelCase or PascalCase to readable)
const formatHeaderName = (field) => {
  return field
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase());
};

// Helper function to build GraphQL mutation queries dynamically
export const generateMutationQuery = (
  mutationName,
  variables,
  mutationObjectName,
  isBulk
) => {
  let args = "";

  // Helper function to format the values based on their datatype
  const formatValue = (value) => {
    if (typeof value === "string") {
      // Enclose strings in quotes
      return `"${value}"`;
    } else if (Array.isArray(value)) {
      // If it's an array, handle each item based on its type
      return `[${value.map((item) => formatValue(item)).join(", ")}]`;
    } else if (value === null) {
      // Handle null values
      return `null`;
    } else {
      // Return the value as is (for numbers, booleans, etc.)
      return value;
    }
  };

  if (isBulk) {
    // Handle bulk operations (arrays of objects or IDs)
    for (const [key, value] of Object.entries(variables)) {
      args += `${key}: ${formatValue(value)}, `;
    }
  } else {
    // Handle single or object-based mutations
    for (const [key, value] of Object.entries(variables)) {
      args += `${key}: ${formatValue(value)}, `;
    }
  }

  // Remove trailing comma and space
  args = args.trim().replace(/,\s*$/, "");

  // Conditionally include mutationObjectName only if it's provided
  return mutationObjectName
    ? `
    mutation {
      ${mutationName}(${mutationObjectName}: {${args}}) {
        results
        error
      }
    }
  `
    : `
    mutation {
      ${mutationName}(${args}) {
        results
        error
      }
    }
  `;
};

// Dynamic mutation service
export const executeAnyMutation = async (
  mutationName,
  variables,
  mutationObjectName,
  isBulk,
  baseUrl
) => {
  const headers = getAuthHeaders(); // Add authentication headers
  const query = generateMutationQuery(
    mutationName,
    variables,
    mutationObjectName,
    isBulk
  ); // Generate mutation query

  try {
    const response = await axios.post(baseUrl, { query }, { headers });
    return response?.data?.data?.[mutationName];
  } catch (error) {
    return { error: "Failed to execute mutation" };
  }
};
